import { FilterUi } from "./FilterUi.js";
import { Dropdown } from "../../../ui/Dropdown.js";
import { FilterSettings } from "../FilterSettings.js";

export { DropdownFilter };

/**
 * A class that defines the Ui for a Dropdown based AttributeFilter
 * 
 * @author rhess <robin.hess@awi.de>
 * @author sjaswal <shahzeib.jaswal@awi.de>
 * 
 * @memberof vef.map.filters.ui
 */
class DropdownFilter extends FilterUi {

    /**
     * @param {HTMLElement | string} target
     * @param {object} options filter specific options
     * @param {LayerCache} layers Used for included/excluded layers
     */
    constructor(target, options, layers) {

        // apply default options
        options = Object.assign({
            title: "Dropdown Filter",
            allowShowNullValues: true,
            showNullValues: true,
            allowDeselect: true,
            sortFields: true,
            search: true,
            searchPaceholder: "",
            placeholder: "select a filter ...",
            allValues: null,
            fields: [],
        }, options || {});

        super(target, options, layers);

        this.schemaRegex = new RegExp("^y(m(d)?)?$");
        this.settingsClass_ = DropdownFilterSettings;

        this.dropdown_ = new Dropdown(null, {
            placeholder: this.options_.placeholder,
            deselectLabel: this.options_.allValues || null,
            showDeselectItem: this.options_.allowDeselect,
            search: this.options_.search,
            searchPlaceholder: this.options_.searchPaceholder
        });

        this.initFilterElement_();
        this.initValues_();

        // add tool to deselect the dropdown
        if(this.options_.allowDeselect) this.addTool("vef vef-deselect-all", () => {
            this.dropdown_.deselect();
            this.fire("change", this);
        }, "Remove All Filters");

        this.initShowNullButton_();
    }

    /**
     * internal method for initializing the main
     * HTML structure of the element
     * 
     * @private
     */
    initFilterElement_() {
        this.setTitle(this.options_.title);
        const container = this.getContentContainer();
        container.classList.add("dropdown-filter");
        container.style.paddingBottom = "5px";
        this.dropdown_.appendTo(container);
    }

    /**
     * Get the filter's options-object
     */
    getOptions() {
        const selectedItem = this.dropdown_.getSelectedItem();

        const options = Object.assign({}, this.options_);

        // assign active field
        for (let i = 0; i < options.fields.length; ++i) {
            options.fields[i].selected = (selectedItem && (options.fields[i] == selectedItem.item));
        }

        return options;
    }

    /**
     * Internal method for initializing the filter values
     * based on the fields column in the options
     * 
     * @private
     */
    initValues_() {
        if (this.options_.fields && typeof this.options_.fields === "string") {
            let request = this.options_.fields;
            this.fetchValues_(request).then(values => {
                this.options_.fields = values.fields;
                this.initDropdown_();
            })
        } else if (this.options_.fields && Array.isArray(this.options_.fields)) {
            this.initDropdown_();
        } else console.error("Missing filter fields");
    }

    /**
     * internal method for initializing dropdown element
     * 
     * @private
     */
    initDropdown_() {
        if (this.options_.sortFields) this.options_.fields.sort((a, b) => a.title.localeCompare(b.title));

        const items = {};

        let activeItem = null;
        for (let i = 0; i < this.options_.fields.length; ++i) {
            const item = this.options_.fields[i];
            items[item.title] = item;
            if (item.selected) activeItem = item.title;
        }

        this.dropdown_.setItems(items);
        if (activeItem) this.dropdown_.select(activeItem);

        this.dropdown_.on("select", e => {
            this.fire("change", this);
        })
    }

    /**
     * Get the filter object to pass it on to the Layers.
     * 
     * @override
     * @returns {object} filter object
     */
    getActiveFilter() {
        if (this.dropdown_) {
            const item = this.dropdown_.getSelectedItem();

            if (item && item.item) {
                let values = (Array.isArray(item.item.values)) ? item.item.values : [item.item.values];
                values = values.map(value => [item.item.operator, value]);
                if (this.options_.showNullValues) values.push(["null"]);
                return {
                    "type": "Attribute",
                    "column": item.item.column,
                    "values": values,
                    "excludedLayers": this.getExcludedLayers()
                }
            }
        }

        return {};
    }


    /**
     * Update the filter based on an existing filter object. Also updates the filter's Ui.
     * 
     * @override
     * @param {object[] | object} filters
     */
    setActiveFilter(filters) {
        if (!Array.isArray(filters)) filters = [filters];

        for (let i = 0; i < filters.length; ++i) {
            const column = filters[i].column;
            const type = filters[i].type.toLowerCase();
            const values = filters[i].values;
            if ((type == "attribute") && column && values.length) {
                for (let j = 0; j < this.options_.fields.length; ++j) {
                    const item = this.options_.fields[j];
                    if ((item.column == column) && (item.operator == values[0][0]) && (item.values == values[0][1])) {
                        this.dropdown_.select(item.title);
                        this.fire("change", this);
                        return;
                    }
                }
            }
        }
        this.dropdown_.deselect();
    }

    /**
     * Fetch filter fields/values based on the request
     * 
     * @param {String} request 
     * @returns {Array} values
     * @private
     */
    async fetchValues_(request) {
        const response = await fetch(request);
        const values = await response.json();
        return values;
    }

    /**
     * method to reload options after applying settings
     */
    reloadOptions_() {
        this.setTitle(this.options_.title);
        this.toggleToolVisibility("btn-power", this.options_.deactivatable);
        this.toggleToolVisibility("btn-show-null", this.options_.allowShowNullValues);
        this.fire("change", this);
    }
}


class DropdownFilterSettings extends FilterSettings {

    htmlExtension = `
        <div><label><input type="checkbox" name="allow-show-null-values"/> Show "include NULL values" Button</label></div>
    `;

    /**
     * @param {FilterUi} filter
     */
    constructor(filter) {
        super(filter);

        this.query("form").insertAdjacentHTML("beforeend", this.htmlExtension);
    }

    /**
     * Get the currently configured filter options
     */
    getFilterOptions() {
        const options = super.getFilterOptions();

        options.allowShowNullValues = this.query("form input[name='allow-show-null-values']").checked;

        return options;
    }

    /**
     * Update Ui based on the filters current options
     */
    updateFilterOptions() {
        super.updateFilterOptions();
        const options = this.filter.options_;

        this.query("form input[name='allow-show-null-values']").checked = options.allowShowNullValues;
    }

}